import React, { lazy, Suspense, useMemo, useEffect } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { SkeletonHeader } from '../Components/Header/skeleton/SkeletonHeader';
import { RouteSkeleton } from '../Components/Skeleton/RouteSkeleton';
import { AuthProvider, ProtectedRoute, Unauthorized, Login, useAuth, PermissionLevel } from '../features/auth';
import { AccountProvider } from '../store';
import { AssistantProvider } from '../contexts/AssistantContext';
import { AssistantProcessFlow } from '../features/assistants/components';
import { rolePermissionService, UserRole } from '../services/rolePermissionService';
import config from '../config';

import { createLogger } from '../utils/logger';
const logger = createLogger('router');

// Layout Components
const MainLayout = lazy(() =>
  import('../Components/layouts/MainLayout').then((module) => ({ default: module.MainLayout })),
);
const SettingsLayout = lazy(() =>
  import('../Components/layouts/SettingsLayout').then((module) => ({
    default: module.SettingsLayout,
  })),
);
const AccountsLayout = lazy(() =>
  import('../Components/layouts/AccountsLayout').then((module) => ({
    default: module.AccountsLayout,
  })),
);
const MetricsLayout = lazy(() =>
  import('../Components/layouts/MetricsLayout').then((module) => ({
    default: module.MetricsLayout,
  })),
);

// Assistants Components
const AssistantsLayout = lazy(() =>
  import('../Components/Assistants/AssistantsLayout').then((module) => ({
    default: module.AssistantsLayout,
  })),
);
const AssistantDetailLayout = lazy(() =>
  import('../Components/Assistants/AssistantDetailLayout').then((module) => ({
    default: module.AssistantDetailLayout,
  })),
);
const MyAssistantLayout = lazy(() =>
  import('../Components/Assistants/MyAssistantLayout').then((module) => ({
    default: module.MyAssistantLayout,
  })),
);

const AssistantManagement = lazy(() =>
  import(
    '../features/assistant-management/components/AssistantManagement/AssistantManagement'
  ).then((module) => ({
    default: module.AssistantManagement,
  })),
);

// Page Components
const HomePage = lazy(() =>
  import('../features/home').then((module) => ({ default: module.HomePage })),
);
const NotFound = lazy(() =>
  import('../Components/NotFound').then((module) => ({ default: module.NotFound })),
);

// Recovery and Password Reset Pages
const RecoverAccount = lazy(() =>
  import('../Components/RecoverAccount/RecoverAccount').then((module) => ({
    default: module.RecoverAccount,
  })),
);
const ResetPassword = lazy(() =>
  import('../Components/ResetPassword/ResetPassword').then((module) => ({
    default: module.ResetPassword,
  })),
);

// Settings Pages
const Users = lazy(() =>
  import('../Components/Settings/Users/Users').then((module) => ({ default: module.Users })),
);
const UserDetails = lazy(() =>
  import('../Components/Settings/Details/Details').then((module) => ({ default: module.Details })),
);
const UsageFees = lazy(() =>
  import('../Components/Settings/UsageFees/UsageFees').then((module) => ({
    default: module.UsageFees,
  })),
);

// ✅ Accounts Pages - Updated Imports
const Account = lazy(() =>
  import('../features/account/components/Account').then((module) => ({
    default: module.Account,
  })),
);

const NewAccount = lazy(() =>
  import('../features/account/components/AccountNew/AccountNew').then((module) => ({
    default: module.NewAccount,
  })),
);

const AccountManagement = lazy(() =>
  import('../features/account/components/AccountManagement/AccountManagement').then((module) => ({
    default: module.AccountManagement,
  })),
);

// Metrics Pages
const Dashboard = lazy(() =>
  import('../Components/Dashboard/Dashboard').then((module) => ({ default: module.Dashboard })),
);

const Inbox = lazy(() =>
  import('../features/inbox/components/Inbox').then((module) => ({ default: module.Inbox })),
);

// My Assistant Pages
const MyAssistantPersonality = lazy(() =>
  import('../features/assistants/components').then((module) => ({
    default: module.MyAssistantPersonality,
  })),
);

const CreateAssistantPage = lazy(() =>
  import('../pages/Assistants/CreateAssistantPage').then((module) => ({
    default: module.CreateAssistantPage,
  })),
);

const CostCalculatorPage = lazy(() =>
  import('../pages/CostCalculatorPage').then((module) => ({ default: module.CostCalculatorPage })),
);

const Flows = lazy(() =>
  import('../features/assistants/components').then((module) => ({
    default: module.ConversationFlows,
  })),
);
const Training = lazy(() =>
  import('../Components/MyAgent/Knowledge/knowledge').then((module) => ({
    default: module.MyAgentKnowledge,
  })),
);
const MyAssistaMyAssistantChannelsntAppearance = lazy(() =>
  import('../features/assistants/components').then((module) => ({
    default: module.MyAssistantChannels,
  })),
);
const Appearance = lazy(() =>
  import('../features/assistants/components').then((module) => ({
    default: module.Appearance,
  })),
);

const MyAssistantTools = lazy(() =>
  import('../features/assistants/components').then((module) => ({
    default: module.MyAssistantTools,
  })),
);

const AccessVerification = lazy(() =>
  import('../features/access-verification').then((module) => ({
    default: module.AccessVerification,
  })),
);

const EscalationsPage = lazy(() =>
  import('../features/assistants/components/Escalations/EscalationsPage').then((module) => ({
    default: module.EscalationsPage,
  })),
);

const WhatsappAdmin = lazy(() =>
  import('../features/whatsapp-admin').then((module) => ({
    default: module.WhatsappAdmin,
  })),
);

// Queue Management Component
const QueueManagement = lazy(() =>
  import('../pages/QueueManagement/QueueManagementPage').then((module) => ({
    default: module.default,
  })),
);

// Modules Component
const Modules = lazy(() =>
  import('../features/modules/components/Modules').then((module) => ({ default: module.Modules })),
);

// Chatbot Component
const Chatbot = lazy(() =>
  import('../Components/Chatbot/Chatbot').then((module) => ({ default: module.Chatbot })),
);

// Simple component for chatbot with on-premise detection
const ChatbotRoute = () => {
  const { chatbotId } = useParams<{ chatbotId: string }>();
  const isOnPremise = chatbotId === '084e0206-ea8c-45d4-aa47-36ee29908fd3';

  // Memoize URLs to prevent recreation on each render
  const wsUrl = useMemo(
    () => isOnPremise ? 'ws://localhost:3001' : (process.env.REACT_APP_WS_SHARED_URL || 'wss://ws.dev.mind.creai.cloud/'),
    [isOnPremise]
  );

  const voiceWsUrl = useMemo(
    () => process.env.REACT_APP_VOICE_AGENT_WS_URL || 'wss://voice-agent.dev.mind.creai.cloud',
    []
  );

  const urlApi = useMemo(
    () =>
      isOnPremise
        ? 'http://localhost:3001/chatbot'
        : `${process.env.REACT_APP_API_HOST || 'https://api.dev.mind.creai.cloud'}/chatbot`,
    [isOnPremise]
  );

  // Add a visual indicator
  useEffect(() => {
    if (window && (window as any).document) {
      (window as any).document.title = `Chatbot: ${chatbotId}`;
    }
  }, [chatbotId]);

  return (
    <Chatbot
      wsUrl={wsUrl}
      voiceWsUrl={voiceWsUrl}
      urlApi={urlApi}
    />
  );
};

// Smart redirect component that redirects users based on their permissions
const SmartRedirect = () => {
  const { user, loading } = useAuth();

  // Si está cargando, mostrar spinner de carga
  if (loading) {
    return <RouteSkeleton />;
  }

  // Si no hay usuario, algo está mal, redirigir a login
  if (!user?.role) {
    return <Navigate to="/login" replace />;
  }

  // For user role, redirect directly to modules
  if (user.role === 'user') {
    return <Navigate to="modules" replace />;
  }

  // For other roles that have Home permission, redirect to home
  if (['superuser', 'seller', 'admin'].includes(user.role)) {
    return <Navigate to="home" replace />;
  }

  // Default fallback - redirect to unauthorized

  return <Navigate to="/unauthorized" replace />;
};

// Documentation Components
const DocsHub = lazy(() =>
  import('../features/docs/components').then((module) => ({ default: module.DocsHub })),
);

const BackendDocs = lazy(() =>
  import('../features/docs/components').then((module) => ({ default: module.BackendDocs })),
);

const StorybookDocs = lazy(() =>
  import('../features/docs/components').then((module) => ({ default: module.StorybookDocs })),
);

const ApiCollectionsDocs = lazy(() =>
  import('../features/docs/components').then((module) => ({ default: module.ApiCollectionsDocs })),
);

// VoiceAgent Component
const VoiceAgentView = lazy(() =>
  import('../features/voiceagent/components').then((module) => ({
    default: module.VoiceAgentView,
  })),
);

// Metrics Dashboard Component
const MetricsDashboard = lazy(() =>
  import('../features/metrics/components/MetricsDashboard').then((module) => ({
    default: module.MetricsDashboard,
  })),
);

// Evals Dashboard Component
const EvalsDashboard = lazy(() =>
  import('../features/evals/components/EvalsDashboard').then((module) => ({
    default: module.EvalsDashboard,
  })),
);

// Team Overview Component - COMMENTED OUT - NOT READY
//const TeamOverview = lazy(() =>
//   import('../features/team-overview/components/TeamOverview').then((module) => ({ default: module.TeamOverview })),
// );

// Wrapper to extract AssistantID param and pass as prop
const AssistantProcessFlowRoute = () => {
  const { AssistantID } = useParams();
  return <AssistantProcessFlow mode="create" assistantId={AssistantID as string} />;
};

export function AppRouter() {
  // Debug: Log when AppRouter renders
  logger.info('🔄 AppRouter rendering');
  logger.info('🔄 Current location:', window.location.href);

  return (
    <AuthProvider>
      <Suspense fallback={<RouteSkeleton />}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/recoveraccount" element={<RecoverAccount />} />
          <Route path="/resetpassword" element={<ResetPassword />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/voiceagent" element={<VoiceAgentView />} />

          {/* Protected documentation routes */}
          <Route
            path="/docs"
            element={
              <ProtectedRoute requiredRoles={['admin', 'superuser']}>
                <DocsHub />
              </ProtectedRoute>
            }
          />
          <Route
            path="/docs/backend"
            element={
              <ProtectedRoute requiredRoles={['admin', 'superuser']}>
                <BackendDocs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/docs/storybook"
            element={
              <ProtectedRoute requiredRoles={['admin', 'superuser']}>
                <StorybookDocs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/docs/api-collections"
            element={
              <ProtectedRoute requiredRoles={['admin', 'superuser']}>
                <ApiCollectionsDocs />
              </ProtectedRoute>
            }
          />

          {/* Chatbot route */}
          <Route path="/chat/:chatbotId" element={<ChatbotRoute />} />

          {/* Account-based routes */}
          <Route
            path="/accounts/:accountId"
            element={
              <AccountProvider>
                <AssistantProvider>
                  <ProtectedRoute>
                    <MainLayout />
                  </ProtectedRoute>
                </AssistantProvider>
              </AccountProvider>
            }
          >
            {/* Home (default route) */}
            <Route index element={<SmartRedirect />} />
            <Route
              path="home"
              element={
                <ProtectedRoute requiredPermission="/home" requiredLevel={PermissionLevel.EDIT}>
                  <HomePage />
                </ProtectedRoute>
              }
            />

            {/* Settings section */}
            <Route
              path="settings"
              element={
                <ProtectedRoute requiredPermission="/settings">
                  <SettingsLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Users />} />
              <Route path="users" element={<Users />} />
              <Route path="user/:userId" element={<UserDetails />} />
              <Route
                path="usage-fees"
                element={
                  <ProtectedRoute requiredPermission="/settings/usage-fees">
                    <UsageFees />
                  </ProtectedRoute>
                }
              />
              <Route path="billing" element={<div>Billing Page</div>} />
              <Route path="authentication" element={<div>Authentication Page</div>} />
              <Route path="api-access" element={<div>API Access Page</div>} />
              <Route
                path="calculator-cost"
                element={
                  <ProtectedRoute requiredPermission="/settings/calculator-cost">
                    <CostCalculatorPage />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Accounts section */}
            <Route
              path="accounts"
              element={
                <ProtectedRoute requiredPermission="/accounts">
                  <AccountsLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Account />} />
              <Route path="new" element={<NewAccount />} />
              <Route path="management" element={<AccountManagement />} />
            </Route>

            {/* Metrics section */}
            <Route
              path="metrics"
              element={
                <ProtectedRoute requiredRoles={['admin', 'superuser']}>
                  <MetricsLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
            </Route>

            {/* Modules section */}
            <Route
              path="modules"
              element={
                <ProtectedRoute requiredPermission="/modules">
                  <Modules />
                </ProtectedRoute>
              }
            />

            {/* Assistants section */}
            <Route
              path="assistants"
              element={
                <ProtectedRoute requiredPermission="/assistants">
                  <AssistantsLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AssistantManagement />} />
              <Route
                path="new"
                element={
                  <ProtectedRoute requiredPermission="/assistants/new">
                    <CreateAssistantPage />
                  </ProtectedRoute>
                }
              />
              <Route path=":AssistantID" element={<AssistantDetailLayout />}>
                <Route index element={<Navigate to="inbox" replace />} />
                <Route path="inbox" element={<Inbox />} />
                <Route path="management" element={<AssistantManagement />} />
                {/* Metrics/Reports section - COMMENTED OUT - NOT READY */}
                {/* <Route path="metrics" element={
                  <ProtectedRoute requiredRoles={['admin', 'superuser']}>
                    <MetricsLayout />
                  </ProtectedRoute>
                }>
                  <Route index element={<Dashboard />} />
                </Route> */}

                {/* My assistant configuration */}
                <Route
                  path="myassistant"
                  element={
                    <ProtectedRoute requiredPermission="/assistants">
                      <MyAssistantLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="personality" replace />} />
                  <Route
                    path="personality"
                    element={
                      <ProtectedRoute requiredPermission="/assistants/personality">
                        <MyAssistantPersonality mode="edit" />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="flows"
                    element={
                      <ProtectedRoute requiredPermission="/assistants/conversational-flows">
                        <Flows />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="process"
                    element={
                      <ProtectedRoute requiredPermission="/assistants/process-flows">
                        <AssistantProcessFlowRoute />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="knowledge"
                    element={
                      <ProtectedRoute requiredPermission="/assistants/knowledge">
                        <Training />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="channels"
                    element={
                      <ProtectedRoute requiredPermission="/assistants/deploy">
                        <MyAssistaMyAssistantChannelsntAppearance />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="appearance"
                    element={
                      <ProtectedRoute requiredPermission="/assistants/appearance">
                        <Appearance />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="queues"
                    element={
                      <ProtectedRoute requiredPermission="/assistants">
                        <QueueManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="metrics"
                    element={
                      <ProtectedRoute requiredPermission="/assistants/metrics">
                        <MetricsDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="evals"
                    element={
                      <ProtectedRoute requiredPermission="/assistants/evals">
                        <EvalsDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="escalations"
                    element={
                      <ProtectedRoute requiredPermission="/assistants/escalations">
                        <EscalationsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="whatsapp"
                    element={
                      <ProtectedRoute requiredRoles={['superuser']}>
                        <WhatsappAdmin />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="tools"
                    element={
                      <ProtectedRoute requiredRoles={['superuser']}>
                        <MyAssistantTools />
                      </ProtectedRoute>
                    }
                  />
                  {/* <Route path="team-overview" element={
                    <ProtectedRoute requiredPermission="/assistants">
                      <TeamOverview />
                    </ProtectedRoute>
                  } /> */}
                </Route>

                {/* Access & Verification */}
                <Route
                  path="access-verification"
                  element={
                    <ProtectedRoute requiredPermission="/assistants/access-verification">
                      <AccessVerification />
                    </ProtectedRoute>
                  }
                />
              </Route>
            </Route>

            {/* Catch-all route for invalid paths within an account - redirect based on permissions */}
            <Route path="*" element={<SmartRedirect />} />
          </Route>

          {/* Tools Section */}
          <Route
            path="/tools/cost-calculator"
            element={
              <ProtectedRoute>
                <CostCalculatorPage />
              </ProtectedRoute>
            }
          />

          {/* Redirect root to login */}
          <Route path="/" element={<Navigate to="/login" replace />} />

          {/* 404 route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </AuthProvider>
  );
}
